import {
  $isTextNode,
  DOMConversion,
  DOMConversionMap,
  DOMConversionOutput,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical'

import TextEditorTheme from '../themes/TextEditorTheme'

export class ExtendedTextNode extends TextNode {
  constructor(text: string, key?: NodeKey) {
    super(text, key)
  }

  static getType(): string {
    return 'extended-text'
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    return new ExtendedTextNode(node.__text, node.__key)
  }

  static importDOM(): DOMConversionMap | null {
    const importers = TextNode.importDOM()
    return {
      ...importers,
      code: () => ({
        conversion: patchStyleConversion(importers?.code),
        priority: 1,
      }),
      em: () => ({
        conversion: patchStyleConversion(importers?.em),
        priority: 1,
      }),
      span: () => ({
        conversion: patchStyleConversion(importers?.span),
        priority: 1,
      }),
      strong: () => ({
        conversion: patchStyleConversion(importers?.strong),
        priority: 1,
      }),
    }
  }

  static importJSON(serializedNode: SerializedTextNode): TextNode {
    return TextNode.importJSON(serializedNode)
  }

  isSimpleText() {
    return this.__type === 'extended-text' && this.__mode === 0
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'extended-text',
      version: 1,
    }
  }
}

function patchStyleConversion(
  originalDOMConverter?: (node: HTMLElement) => DOMConversion | null
): (node: HTMLElement) => DOMConversionOutput | null {
  return (node) => {
    const original = originalDOMConverter?.(node)
    if (!original) {
      return null
    }
    const originalOutput = original.conversion(node)

    if (!originalOutput) {
      return originalOutput
    }

    return {
      ...originalOutput,
      forChild: (lexicalNode, parent) => {
        const originalForChild = originalOutput?.forChild ?? ((x) => x)
        const result = originalForChild(lexicalNode, parent)
        if ($isTextNode(result)) {
          const classes = node.className.split(' ').filter(Boolean)

          if (classes.length) {
            result.setFormat(0)

            classes.forEach((className) => {
              switch (className) {
                case TextEditorTheme.text.bold:
                  result.toggleFormat('bold')
                  break
                case TextEditorTheme.text.italic:
                  result.toggleFormat('italic')
                  break
                case TextEditorTheme.text.underline:
                  result.toggleFormat('underline')
                  break
                case TextEditorTheme.text.strikethrough:
                  result.toggleFormat('strikethrough')
                  break
                case TextEditorTheme.text.underlineStrikethrough:
                  result.toggleFormat('underline')
                  result.toggleFormat('strikethrough')
                  break
                case TextEditorTheme.text.code:
                  result.toggleFormat('code')
                  break
              }
            })
          }
        }
        return result
      },
    }
  }
}
